<script setup>
/**
 * "For now" (ease C3, spec §6 P-6.9): something true for a while, like a holiday or a closed
 * branch. The assistant uses it until the last day she picks, then it removes itself.
 *
 * NO AI ANYWHERE, so nothing here is priced. A note is her own words, rendered escaped, in
 * its own direction. The last day is a day in the account's own zone (the server decides
 * "today"), so the date box offers today to a year ahead and the server has the last word
 * (`invalid_day` comes back pinned on the field).
 *
 * TEN AT MOST, and the eleventh is refused with a sentence, not hidden: a box that silently
 * stops accepting notes reads as broken. Extend is a new last day on the same note.
 * Remove puts it in Recently removed for 30 days, like every other removal.
 */
import { computed, ref } from 'vue';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import WasloInput from './WasloInput.vue';
import WasloTextArea from './WasloTextArea.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { toIntlLocale } from '../helpers/intl.js';
import { wasloApi } from '../api/client.js';

const props = defineProps({
  accountId: { type: Number, default: null },
});

const emit = defineEmits(['changed']);

const { t, locale } = useWasloI18n();

const MAX_NOTICE_CHARS = 500;

const list = useWasloApi(
  signal => wasloApi.getNotices(props.accountId, signal),
  { isEmpty: () => false }
);
const notices = computed(() => list.data.value ?? []);

/** `YYYY-MM-DD` of a date in the viewer's own calendar: the input's minimum only. */
const dayOf = date =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
const today = dayOf(new Date());
const yearAhead = dayOf(new Date(Date.now() + 366 * 24 * 60 * 60 * 1000));

const text = ref('');
const endsOn = ref(today);
const isSaving = ref(false);
const fields = ref({});
const failure = ref('');
const extending = ref(null);
const extendTo = ref('');

/** "Removes itself after 3 October", in her language, read as a calendar day. */
const formatDay = day => {
  const [y, m, d] = String(day).split('-').map(Number);
  if (!y || !m || !d) return day;
  return new Intl.DateTimeFormat(toIntlLocale(locale.value), {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
};

const sentenceOf = error => {
  if (error?.fields) {
    fields.value = error.fields;
    return '';
  }
  if (error?.status === 409) return t('WASLO.NOTICES.FULL');
  return t('WASLO.NOTICES.FAILED');
};

const add = async () => {
  if (isSaving.value || props.accountId === null || text.value.trim() === '') {
    return;
  }
  isSaving.value = true;
  fields.value = {};
  failure.value = '';
  try {
    await wasloApi.addNotice(props.accountId, {
      text: text.value.trim(),
      endsOn: endsOn.value,
    });
    text.value = '';
    endsOn.value = today;
    list.reload();
    emit('changed');
  } catch (error) {
    failure.value = sentenceOf(error);
  } finally {
    isSaving.value = false;
  }
};

const startExtend = notice => {
  extending.value = notice.id;
  extendTo.value = notice.endsOn;
};

const saveExtend = async notice => {
  if (isSaving.value || props.accountId === null) return;
  isSaving.value = true;
  failure.value = '';
  try {
    await wasloApi.updateNotice(props.accountId, notice.id, {
      endsOn: extendTo.value,
    });
    extending.value = null;
    list.reload();
    emit('changed');
  } catch (error) {
    failure.value = sentenceOf(error) || t('WASLO.NOTICES.FAILED');
  } finally {
    isSaving.value = false;
  }
};

const remove = async notice => {
  if (isSaving.value || props.accountId === null) return;
  isSaving.value = true;
  failure.value = '';
  try {
    await wasloApi.removeNotice(props.accountId, notice.id);
    list.reload();
    emit('changed');
  } catch (error) {
    failure.value = sentenceOf(error);
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ reload: () => list.reload() });
</script>

<template>
  <section class="flex flex-col gap-4" data-waslo-notices>
    <SettingsHeader
      :heading="t('WASLO.NOTICES.HEADING')"
      :description="t('WASLO.NOTICES.DESCRIPTION')"
    />

    <Banner
      v-if="list.error.value"
      color="slate"
      data-waslo-notices-unavailable
    >
      {{ t('WASLO.NOTICES.UNAVAILABLE') }}
    </Banner>

    <ul
      v-if="notices.length"
      class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
      data-waslo-notices-list
    >
      <li
        v-for="notice in notices"
        :key="notice.id"
        class="flex flex-col gap-2 px-5 py-4"
        :data-waslo-notice="notice.id"
      >
        <p
          class="mb-0 text-sm break-words whitespace-pre-line text-n-slate-12"
          dir="auto"
        >
          <bdi>{{ notice.text }}</bdi>
        </p>
        <p class="mb-0 text-xs text-n-slate-11" data-waslo-notice-ends>
          {{ t('WASLO.NOTICES.ENDS', { date: formatDay(notice.endsOn) }) }}
        </p>
        <div
          v-if="extending === notice.id"
          class="flex flex-wrap items-end gap-2"
        >
          <WasloInput
            v-model="extendTo"
            type="date"
            :min="today"
            :max="yearAhead"
            :label="t('WASLO.NOTICES.ENDS_LABEL')"
            data-waslo-notice-extend-day
          />
          <Button
            size="sm"
            :label="t('WASLO.NOTICES.SAVE')"
            :is-loading="isSaving"
            data-waslo-notice-extend-save
            @click="saveExtend(notice)"
          />
          <Button
            size="sm"
            variant="ghost"
            color="slate"
            :label="t('WASLO.NOTICES.CANCEL')"
            @click="extending = null"
          />
        </div>
        <div v-else class="flex flex-wrap gap-2">
          <Button
            size="xs"
            variant="faded"
            color="slate"
            :label="t('WASLO.NOTICES.EXTEND')"
            data-waslo-notice-extend
            @click="startExtend(notice)"
          />
          <Button
            size="xs"
            variant="ghost"
            color="ruby"
            :label="t('WASLO.NOTICES.REMOVE')"
            data-waslo-notice-remove
            @click="remove(notice)"
          />
        </div>
      </li>
    </ul>

    <form
      class="flex flex-col gap-3"
      data-waslo-notice-form
      @submit.prevent="add"
    >
      <WasloTextArea
        v-model="text"
        :label="t('WASLO.NOTICES.TEXT_LABEL')"
        :placeholder="t('WASLO.NOTICES.TEXT_PLACEHOLDER')"
        :max-length="MAX_NOTICE_CHARS"
        :message="fields.text || ''"
        message-type="error"
        min-height="3.5rem"
        data-waslo-notice-text
      />
      <div class="flex flex-wrap items-end gap-2">
        <WasloInput
          v-model="endsOn"
          type="date"
          :min="today"
          :max="yearAhead"
          :label="t('WASLO.NOTICES.ENDS_LABEL')"
          :message="fields.endsOn || ''"
          message-type="error"
          data-waslo-notice-day
        />
        <Button
          type="submit"
          size="sm"
          :label="t('WASLO.NOTICES.ADD')"
          :is-loading="isSaving"
          :disabled="text.trim() === ''"
          data-waslo-notice-add
        />
      </div>
    </form>

    <p
      v-if="failure"
      class="mb-0 text-sm text-n-ruby-11"
      aria-live="polite"
      data-waslo-notices-failure
    >
      {{ failure }}
    </p>
  </section>
</template>
